import { inject, Injectable } from '@angular/core';
import { LogInstanceToken } from '@evorait/pace-logging';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import {
    NotificationStatusInterface,
    NotificationStatusListModel,
} from '../../interfaces/notification-status.interface';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationStatusModel } from '../../models/low-level/notification-status.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { StatusModel, StatusModelFromJson } from '../../models/low-level/status.model';
import {
    addStatusToNotificationEntry,
    cancellationConfirmationRemoved,
    cancellationConfirmationSaved,
    hydrateNotifications,
    markNotificationCompleted,
    notificationRestoredFromStorage,
    persistNotificationsRequested,
    removeExpiredCompletedNotifications,
    setNotificationCompletions,
    setNotificationStatusFields,
    updateNotificationStatusFields,
} from '../../state/notification/actions/notification.actions';
import {
    completedListModelToCompletions,
    completedNotificationsToListModel,
    entryToNotificationStatusModel,
    notificationIdToState,
    notificationStatusFieldsToModel,
    notificationStatusModelToFields,
    statusModelToStatusRecord,
} from '../../state/notification/notification-collection.converters';
import {
    defaultNotificationStatusFields,
    NotificationState,
} from '../../state/notification/notification-collection.state';
import {
    selectCancellationConfirmedById,
    selectCompletions,
    selectInProgressOrToBeSignedEntry,
    selectLatestStatusRecordForId,
    selectNotificationCompletedById,
    selectNotificationEntries,
    selectNotificationEntryById,
    selectNotificationEntryIndexOrNextForId,
    selectStatusExistsForId,
    selectStatusRecordByCodeForId,
} from '../../state/notification/selectors/notification.selector';
import { EventService } from '../event/event.service';

/**
 * NgRx-backed implementation of {@link NotificationStatusInterface}. Every field it reads and writes
 * — the recorded statuses, the escalation/cancellation/unread flags, the recently-completed
 * notifications and the cancellation-confirmation record — lives in the notification store slice,
 * on the notification each one belongs to. Every read uses a selector and every mutation dispatches
 * an action; `NotificationEffects` mirrors the slice to the concurrency store.
 *
 * Because the status sits on the notification, a write for a notification the slice does not hold
 * is dropped: it has nowhere to live, and the next load would discard it anyway. That makes this
 * service dependent on the notification slice being populated, which `enableNgRx` guarantees by
 * serving both from their slices together.
 *
 * The service acts as a thin translation layer between the legacy model types used by callers and
 * the serializable state types in the store. Model conversion happens exclusively at the public
 * method boundary — internal logic operates on state types only.
 */
@Injectable({
    providedIn: 'root',
})
export class NotificationStatusNgrxService implements NotificationStatusInterface {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly eventService = inject(EventService);
    private readonly log = inject(LogInstanceToken);

    public constructor() {
        this.eventService.synchronizationRequested$.pipe(map(() => this.init())).subscribe();
    }

    /**
     * Re-reads the persisted slice and applies the completed-notification retention policy once the
     * read is done. The read is awaited through `notificationRestoredFromStorage`, which
     * `NotificationEffects` emits once the restored entries are in the slice — a failed read
     * settles it too, since it arrives as a hydration carrying no data. `merge` subscribes to its
     * sources in order, which is what puts the listener in place before the dispatch that triggers
     * the read.
     */
    public init(): Observable<void> {
        // Listener first, dispatch second, both on the call rather than on subscription: callers
        // subscribe after the read may already have settled, and the `synchronizationRequested$`
        // handler in the constructor never subscribes at all — it relies on `init()` triggering the
        // read by itself. `take(1)` ends the bookkeeping subscription as soon as the read lands.
        const restored$ = this.actions$.pipe(
            ofType(notificationRestoredFromStorage),
            take(1),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        restored$.subscribe();
        this.store.dispatch(hydrateNotifications());
        return restored$.pipe(
            map(() => {
                // The reducer applies the retention policy; whoever shows the resulting list reads
                // it back from the slice through `completedNotifications$()`.
                this.store.dispatch(removeExpiredCompletedNotifications());
                return undefined;
            }),
        );
    }

    public getLatestNotificationStatusForId(notificationId: NotificationIdentifierModel): StatusModel | undefined {
        const status = this.store.selectSignal(selectLatestStatusRecordForId(notificationIdToState(notificationId)))();
        return status ? new StatusModelFromJson(status as never) : undefined;
    }

    public saveNotificationStatus(
        notificationId: NotificationIdentifierModel,
        newStatus: StatusModel,
    ): Observable<boolean> {
        return of(this.addStatusIfMissing(notificationId, newStatus));
    }

    public addMissingStatusToList(notificationId: NotificationIdentifierModel, newStatus: StatusModel): void {
        this.addStatusIfMissing(notificationId, newStatus);
    }

    public setNotificationStatusList(notificationStatusList: NotificationStatusModel[]): void {
        this.store.dispatch(
            setNotificationStatusFields({ entries: notificationStatusList.map(notificationStatusModelToFields) }),
        );
    }

    public saveNotificationStatusList(): Observable<void> {
        this.store.dispatch(persistNotificationsRequested());
        return of(undefined);
    }

    public syncNotificationStatusList(): Observable<NotificationStatusListModel> {
        return of({
            notificationStatusList: this.store
                .selectSignal(selectNotificationEntries)()
                .map(entryToNotificationStatusModel),
            lastSavedTimestamp: 0,
        });
    }

    public loadRecentlyCompletedNotifications(): Observable<NotificationCompletedListModel> {
        // The reducer applies the retention policy; logCompletedNotifications$ logs the result.
        this.store.dispatch(removeExpiredCompletedNotifications());
        return of(completedNotificationsToListModel(this.store.selectSignal(selectCompletions)()));
    }

    public saveRecentlyCompletedNotifications(
        completedNotificationState: NotificationCompletedListModel,
    ): Observable<void> {
        this.store.dispatch(
            setNotificationCompletions({ completions: completedListModelToCompletions(completedNotificationState) }),
        );
        return of(undefined);
    }

    public addNotificationToRecentlyCompletedList(notification: NotificationModel): Observable<void> {
        this.log.debug(`${this.constructor.name}: addNotificationToRecentlyCompletedList`, {
            notificationId: notification.identifier,
        });
        this.store.dispatch(
            markNotificationCompleted({
                id: notificationIdToState(notification.identifier),
                completion: {
                    status: notification.updatedStatus.statusCode,
                    timestampCompleted: Date.now(),
                },
            }),
        );
        return of(undefined);
    }

    public isNotificationCompleted(notificationId: NotificationIdentifierModel): Observable<boolean> {
        return of(this.store.selectSignal(selectNotificationCompletedById(notificationIdToState(notificationId)))());
    }

    public findNotificationModelIndexById(notificationId: NotificationIdentifierModel): number {
        return this.store.selectSignal(
            selectNotificationEntryIndexOrNextForId(notificationIdToState(notificationId)),
        )();
    }

    /**
     * The notification's status. A notification the slice does not hold reports the default status
     * — nothing has been recorded for it — rather than throwing: the legacy contract is that this
     * always answers with a model.
     *
     * @param notificationId
     */
    public getNotificationStatusById(notificationId: NotificationIdentifierModel): NotificationStatusModel {
        const entry = this.entryFor(notificationId);
        return entry
            ? entryToNotificationStatusModel(entry)
            : notificationStatusFieldsToModel(notificationIdToState(notificationId), defaultNotificationStatusFields());
    }

    public getNotificationStatusByStatusType(
        notificationId: NotificationIdentifierModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): StatusModel | undefined {
        const status = this.store.selectSignal(
            selectStatusRecordByCodeForId(notificationIdToState(notificationId), statusCode),
        )();
        return status ? new StatusModelFromJson(status as never) : undefined;
    }

    public getEscalated(notificationId: NotificationIdentifierModel): boolean {
        return this.entryFor(notificationId)?.escalated ?? false;
    }

    public saveEscalated(notificationId: NotificationIdentifierModel, escalated: boolean): Observable<void> {
        this.store.dispatch(
            updateNotificationStatusFields({ id: notificationIdToState(notificationId), changes: { escalated } }),
        );
        return of(undefined);
    }

    public getCancelled(notificationId: NotificationIdentifierModel): string {
        return this.entryFor(notificationId)?.cancelled ?? '';
    }

    public saveCancelled(notificationId: NotificationIdentifierModel, cancelledStatus: string): void {
        this.store.dispatch(
            updateNotificationStatusFields({
                id: notificationIdToState(notificationId),
                changes: { cancelled: cancelledStatus },
            }),
        );
    }

    public getUnreadIndicator(notificationId: NotificationIdentifierModel): boolean {
        return this.entryFor(notificationId)?.unread ?? true;
    }

    public saveUnreadIndicator(notificationId: NotificationIdentifierModel, unread: boolean): Observable<void> {
        this.store.dispatch(
            updateNotificationStatusFields({ id: notificationIdToState(notificationId), changes: { unread } }),
        );
        return of(undefined);
    }

    /**
     * Paused notifications are no longer supported, therefore returning false
     */
    public getPausedIndicator(): boolean {
        return false;
    }

    /**
     * Paused notifications are no longer supported, therefore resolving immediately
     */
    public savePausedIndicator(): Observable<void> {
        return of(undefined);
    }

    public saveCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): Observable<void> {
        this.store.dispatch(cancellationConfirmationSaved({ id: notificationIdToState(notificationId) }));
        return of(undefined);
    }

    public removeCancellationConfirmationForInprogress(): void {
        this.store.dispatch(cancellationConfirmationRemoved());
    }

    public async getCancellationConfirmationForInprogress(
        notificationId: NotificationIdentifierModel,
    ): Promise<boolean> {
        return this.store.selectSignal(selectCancellationConfirmedById(notificationIdToState(notificationId)))();
    }

    /**
     * A no-op: the status lives on the notification it belongs to, so a notification the backend no
     * longer sends takes its status with it when the next load replaces the collection (see
     * `carryOverStatusFields`). Nothing is left over to prune — the completion records live apart
     * from the entries, are kept deliberately, and age out through `removeExpiredCompletedNotifications`.
     *
     * @param notificationArray the notifications to keep, which the store already knows
     */
    public removeOldNotificationsStatus(notificationArray: NotificationModel[]): Observable<void> {
        this.log.debug(
            `${this.constructor.name}: removeOldNotificationsStatus: nothing to prune for ` +
                `${notificationArray.length} notifications, the load already did it`,
        );
        return of(undefined);
    }

    public getNotificationByStatusInProgressOrToBeSigned(): NotificationStatusModel | undefined {
        const entry = this.store.selectSignal(selectInProgressOrToBeSignedEntry)();
        return entry ? entryToNotificationStatusModel(entry) : undefined;
    }

    /**
     * Paused notifications are no longer supported, therefore returning undefined as there cannot be any paused entry
     * anymore
     */
    public getNotificationStatusIsPaused(): NotificationStatusModel | undefined {
        return undefined;
    }

    /**
     * Append `newStatus` unless the notification already carries that status code, and report
     * whether it was added. The reducer would ignore a duplicate anyway, but skipping the dispatch
     * also avoids the redundant persist that every dispatch triggers. `logStatusAdded$` logs the
     * write.
     *
     * @param notificationId identifies the notification to append to
     * @param newStatus the status to append
     * @returns `true` when the status was appended, `false` when it was already present
     */
    private addStatusIfMissing(notificationId: NotificationIdentifierModel, newStatus: StatusModel): boolean {
        const id = notificationIdToState(notificationId);
        if (this.store.selectSignal(selectStatusExistsForId(id, newStatus.statusCode))()) {
            return false;
        }
        this.store.dispatch(addStatusToNotificationEntry({ id, status: statusModelToStatusRecord(newStatus) }));
        return true;
    }

    /**
     * The stored notification for `notificationId`, or `undefined` when the slice does not hold it.
     *
     * @param notificationId
     */
    private entryFor(notificationId: NotificationIdentifierModel): NotificationState | undefined {
        return this.store.selectSignal(selectNotificationEntryById(notificationIdToState(notificationId)))();
    }
}
